import React, { useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import { MUNICIPALITY_OPTIONS, REGION_OPTIONS } from '../utils/courseLocationData';

export default function CompetitionCoursePickerDialog({
  open,
  courses,
  selectedCourseIDs,
  onClose,
  onToggleCourse,
  onOpenAddCourse,
  onOpenEditCourse,
}) {
  const [nameFilter, setNameFilter] = useState('');
  const [municipalityFilter, setMunicipalityFilter] = useState('');
  const [regionFilter, setRegionFilter] = useState('');

  const selectedSet = useMemo(() => new Set(selectedCourseIDs.map(Number)), [selectedCourseIDs]);

  const filteredCourses = useMemo(() => {
    const normalizedName = nameFilter.trim().toLowerCase();

    return courses.filter((course) => {
      const matchesName = normalizedName === ''
        || course.name.toLowerCase().includes(normalizedName);
      const matchesMunicipality = municipalityFilter === '' || course.municipality === municipalityFilter;
      const matchesRegion = regionFilter === '' || course.region === regionFilter;

      return matchesName && matchesMunicipality && matchesRegion;
    });
  }, [courses, municipalityFilter, nameFilter, regionFilter]);

  const selectedCourses = useMemo(
    () => courses.filter((course) => selectedSet.has(Number(course.courseID))),
    [courses, selectedSet]
  );

  function resetFilters() {
    setNameFilter('');
    setMunicipalityFilter('');
    setRegionFilter('');
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Valitse kilpailun radat</DialogTitle>
      <DialogContent>
        <div className="mt-2 flex flex-col gap-4">
          <div className="rounded-2xl border border-white/10 bg-slate-950/5 p-4">
            <div className="grid gap-4 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)_auto] md:items-end">
              <TextField
                label="Hae nimellä"
                variant="filled"
                value={nameFilter}
                onChange={(event) => setNameFilter(event.target.value)}
                fullWidth
              />

              <FormControl variant="filled" fullWidth>
                <InputLabel id="course-filter-municipality-label">Paikkakunta</InputLabel>
                <Select
                  labelId="course-filter-municipality-label"
                  value={municipalityFilter}
                  onChange={(event) => setMunicipalityFilter(event.target.value)}
                >
                  <MenuItem value="">Kaikki paikkakunnat</MenuItem>
                  {MUNICIPALITY_OPTIONS.map((municipality) => (
                    <MenuItem key={municipality} value={municipality}>{municipality}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl variant="filled" fullWidth>
                <InputLabel id="course-filter-region-label">Maakunta</InputLabel>
                <Select
                  labelId="course-filter-region-label"
                  value={regionFilter}
                  onChange={(event) => setRegionFilter(event.target.value)}
                >
                  <MenuItem value="">Kaikki maakunnat</MenuItem>
                  {REGION_OPTIONS.map((region) => (
                    <MenuItem key={region} value={region}>{region}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button type="button" variant="outlined" color="secondary" onClick={resetFilters}>Tyhjennä</Button>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-slate-600">
                Näytetään {filteredCourses.length} / {courses.length} rataa. Valittuna {selectedCourseIDs.length}.
              </p>
              <Button type="button" variant="contained" color="primary" onClick={onOpenAddCourse}>Lisää uusi rata</Button>
            </div>
          </div>

          {selectedCourses.length ? (
            <div className="rounded-2xl border border-cyan-200 bg-cyan-50/70 p-4">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-cyan-700">Valitut radat</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {selectedCourses.map((course) => (
                  <span key={course.courseID} className="rounded-full bg-cyan-600 px-3 py-1 text-sm font-semibold text-white">
                    {course.name}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          <div className="grid max-h-[52vh] gap-3 overflow-y-auto pr-1 md:grid-cols-2">
            {filteredCourses.length ? filteredCourses.map((course) => {
              const isSelected = selectedSet.has(Number(course.courseID));

              return (
                <div
                  key={course.courseID}
                  className={`rounded-2xl border p-4 transition ${
                    isSelected
                      ? 'border-cyan-300 bg-cyan-50 shadow-lg shadow-cyan-900/10'
                      : 'border-slate-200 bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => onToggleCourse(course.courseID)}
                      className="flex flex-1 items-start gap-3 text-left"
                    >
                      <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border text-xs font-bold ${
                        isSelected
                          ? 'border-cyan-600 bg-cyan-600 text-white'
                          : 'border-slate-300 bg-slate-100 text-transparent'
                      }`}
                      >
                        ✓
                      </span>

                      <span className="min-w-0">
                        <span className="block text-base font-semibold text-slate-900">{course.name}</span>
                        <span className="mt-1 block text-sm text-slate-600">{course.municipality}, {course.region}</span>
                        {course.createdByName ? (
                          <span className="mt-2 block text-xs uppercase tracking-[0.2em] text-slate-400">
                            Lisännyt {course.createdByName}
                          </span>
                        ) : null}
                      </span>
                    </button>

                    {course.canEdit ? (
                      <Button type="button" variant="outlined" color="warning" onClick={() => onOpenEditCourse(course)}>
                        Muokkaa
                      </Button>
                    ) : null}
                  </div>
                </div>
              );
            }) : (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500 md:col-span-2">
                Hakuehdoilla ei löytynyt ratoja. Lisää uusi rata, jos sitä ei vielä löydy listalta.
              </div>
            )}
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">Valmis</Button>
      </DialogActions>
    </Dialog>
  );
}